import { z } from 'zod';

import {
    formFieldSchema,
    promoFieldSchema,
    labelsSchema,
    formCascadeRuleSchema,
    fieldRuleSchema,
} from './feFieldSchemas';
import { promoLibrarySchema } from './promoLibrarySchema';

/**
 * FE contract for the backend `GET /tpo_gen/config/` response.
 *
 * This schema is the single source of truth for both the runtime check
 * (`customerConfigSchema.safeParse`) and the TypeScript types (`z.infer`).
 * When the backend contract changes, update this file only.
 *
 * Rules:
 * - Load-bearing fields are REQUIRED (no `.optional()`/`.catch()`) so a missing
 *   one fails loudly at the config gate instead of silently breaking the UI.
 * - Genuinely tolerant fields use `.optional().default(...)`.
 * - Unknown extra fields are stripped by default (forward-compatible).
 */

// Metric DEFINITION (`metrics[]`). Drives the editable promo/spend grid (split
// by `group`) and supplies definitions joined into the report `metric_views`.
export const applicableWhenConditionSchema = z.object({
    attribute: z.string(),
    operator: z.string(),
    values: z.array(z.string()),
});

/**
 * Visibility role for a metric (backend `role`). Governs whether a metric is
 * shown to the user, independent of `applicable_when` / `default_visible`:
 * - `computed` (default) / `input` — displayable + selectable + used in fe_formula.
 * - `fe_hidden` — NEVER displayed, but STILL fed into client-side fe_formula math.
 * - `internal` — stripped server-side (never received); declared only for completeness.
 * The field may be absent; a missing role is treated as `computed` (displayable).
 */
export const metricRoleSchema = z.enum(['computed', 'input', 'fe_hidden', 'internal']);

export const metricSchema = z.object({
    // Load-bearing — required.
    key: z.string().min(1),
    display_name: z.string().min(1),
    group: z.string().min(1),
    order: z.number(),
    default_visible: z.boolean(),
    editable: z.boolean(),
    precision: z.number(),
    // Visibility role — optional; missing is treated as `computed` (displayable).
    role: metricRoleSchema.optional(),
    applicable_promotion_types: z.array(z.string()).optional(),
    applicable_when: z.array(applicableWhenConditionSchema).optional(),
    // Tolerant — nullable + defaulted (backend may send these as null).
    min_value: z.number().nullable().optional().default(null),
    max_value: z.number().nullable().optional().default(null),
    currency_prefix: z.string().nullable().optional().default(''),
    currency_suffix: z.string().nullable().optional().default(''),
    // Optional metadata (backend may send these as null).
    data_type: z.string().nullable().optional(),
    formula: z.string().nullable().optional(),
    fe_formula: z.string().nullable().optional(),
    depends_on: z.array(z.string()).nullable().optional(),
    source: z.string().optional(),
    render_mode: z.string().optional(),
});

// `metric_views` is OPTIONAL and NOT consumed at runtime (the MetricPage KPI
// catalog is built from `metrics[]` group `kpi_metrics`). It is validated only
// as a loose, tolerant array so that varied/partial backend shapes (e.g.
// metadata columns sending `metric_key: null`) never block the module.
export const metricViewSchema = z.record(z.unknown());

/**
 * Backend-provided static FE config (the former static `tpoGenConfig` object).
 *
 * The form-field arrays (`eventDetailsFields`, `promoDetailsFields`,
 * `promoFields`, `planFields`) and `labels` are validated at render-critical
 * strictness via the schemas in `feFieldSchemas.ts` so a malformed entry fails
 * loud at the gate instead of crashing a component. The remaining keys stay
 * coarse (right container type only); their deep shapes are cast to the FE TS
 * types inside `feConfigAdapter`. `.passthrough()` keeps any extra UI keys the
 * backend adds ahead of the FE, and the strict element schemas above also
 * passthrough save-only fields.
 */
export const feConfigSchema = z
    .object({
        showEditableIndicators: z.boolean().optional().default(false),
        // When true, updating an event marks any open promo tabs' cached data as
        // stale so it re-fetches the next time the user opens that promo tab.
        refetchPromoOnEventUpdate: z.boolean().optional().default(false),
        // When true, saving/updating a promo marks every *other* open promo tab's
        // cached data as stale (the just-saved tab is refreshed immediately as
        // before) so each re-fetches the next time the user opens that tab.
        refetchOtherPromosOnPromoSave: z.boolean().optional().default(false),
        // When true, a new promo's (disabled) date fields mirror the event's
        // current form dates, matched by apiKey. Off => no event->promo date carry.
        carryEventDatesToPromo: z.boolean().optional().default(false),
        // When true, a saved event with no promos blocks close/navigation (No-Promos
        // modal + breadcrumb lock). Off => user can leave a promo-less saved event.
        requirePromoForEvent: z.boolean().optional().default(true),
        // PromoActionBar label on the event promo list. Same shape as calendar
        // bar field entries: `field` is the lodash path on the promo summary;
        // optional `resolveLabelFrom` looks up the display label in
        // `fieldValueLabelMaps` (e.g. mechanic value → 'TPR').
        promoListLabelField: z
            .object({
                field: z.string().min(1),
                resolveLabelFrom: z.string().min(1).optional(),
            })
            .optional()
            .default({ field: 'promo_name' }),
        eventDetailsFields: z.array(formFieldSchema),
        promoDetailsFields: z.array(formFieldSchema),
        promoFields: z.array(promoFieldSchema).min(1),
        upliftConfig: z.record(z.unknown()),
        formCascadeRules: z.array(formCascadeRuleSchema).optional().default([]),
        fieldRules: z.array(fieldRuleSchema).optional().default([]),
        // Metric grid applicability rules (same FieldRule shape; target-only
        // `metric:<key>` scope). Validated like fieldRules; consumed via
        // useTpoGenConfig().metricRules.
        metricRules: z.array(fieldRuleSchema).optional().default([]),
        planFields: z.array(formFieldSchema).min(1),
        planIndexColumns: z.array(z.record(z.unknown())).min(1),
        calendarIntervalOptions: z.array(z.record(z.unknown())),
        defaultCalendarInterval: z.enum(['weekly', 'monthly', 'quarterly']).optional().default('weekly'),
        // First day of the calendar week; aligns the monthly-view week dividers
        // (e.g. Danone starts weeks on Monday) instead of a flat 7-day count.
        weekStartDay: z.enum(['Sunday', 'Monday', 'Saturday']).optional().default('Sunday'),
        allDefaultColumns: z.array(z.record(z.unknown())),
        // Both menus are authored as divider-separated SECTIONS (an array of
        // arrays). The dividers themselves are derived at render time, so a
        // section is the unit of grouping and of per-view visibility.
        defaultRowActionItems: z.array(z.array(z.record(z.unknown()))),
        planActionMenuItems: z.array(z.array(z.record(z.unknown()))),
        // Per-surface visibility for the plan-action menu (including the
        // injected `edit_plan` row). Optional: an absent map keeps every item
        // on every surface, and an absent key inside it does the same.
        planActionMenuViews: z.record(z.array(z.string())).optional(),
        labels: labelsSchema,
        planIndexFilterKeys: z.array(z.record(z.unknown())),
        filterDimensionInfo: z.record(z.unknown()),
        calendarFilterKeys: z.array(z.record(z.unknown())),
        calendarFilterDimensionInfo: z.record(z.unknown()),
        // MetricPage filter registry, decoupled from the Calendar filter. Optional:
        // `feConfigAdapter` falls back to the calendar equivalents when a customer
        // config omits them, so absence never blocks the module or changes behaviour.
        metricFilterKeys: z.array(z.record(z.unknown())).optional(),
        metricFilterDimensionInfo: z.record(z.unknown()).optional(),
        calendarGroupByOptions: z.array(z.record(z.unknown())),
        // MetricPage toolbar/view config. Optional: adapted (and defaulted) in
        // `feConfigAdapter` against the `MetricPageConfig` type.
        metricPageConfig: z.record(z.unknown()).optional(),
        promoLibraryConfig: z.record(z.unknown()).optional(),
        optimizePlanConfig: z.record(z.unknown()).optional(),
        optimizeResultConfig: z.record(z.unknown()).optional(),
        infeasiblePlanConfig: z.record(z.unknown()).optional(),
        calendarBarFieldMap: z.record(z.unknown()),
        importEventFromPlanConfig: z.record(z.unknown()).optional(),
        // Per-field maps of raw API value -> display label. Optional: absent means
        // all display surfaces fall back to startCase. Entries opt in via
        // `resolveLabelFrom` on filter keys, columns, bar fields, etc.
        fieldValueLabelMaps: z.record(z.record(z.string())).optional().default({}),
        // Server-backed edit lock. Optional and defaulted on, so an existing
        // customer config keeps the feature without needing a config change;
        // `enabled: false` is the rollout kill-switch.
        editLock: z
            .object({
                enabled: z.boolean().optional().default(true),
                // Seconds remaining at which the session pill switches to its
                // warning state and the extend prompt opens on its own.
                warningThresholdSec: z.number().positive().optional().default(600),
            })
            .optional()
            .default({}),
    })
    .passthrough();

export const metricGroupSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    order: z.number().optional().default(0),
});

export const promotionTypeSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    fund_type: z.string().optional().default(''),
    properties: z.record(z.unknown()).optional().default({}),
});

export const tacticSchema = z.object({
    key: z.string().min(1),
    code: z.string().optional().default(''),
    display_name: z.string().min(1),
});

// Generic business-attribute declaration (`entity_attributes[]`). Drives the
// dynamic `attributes` codec: which keys live in the nested `attributes` object
// per entity level, and where their dropdown options come from. New attributes
// can be added here (backend) + a matching field config (`apiKey: 'attributes.<key>'`)
// without touching any payload/response hook.
export const entityAttributeSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    level: z.string().min(1), // 'plan' | 'event' | 'promotion'
    data_type: z.string().min(1),
    required: z.boolean().optional().default(false),
    options_source: z.string().nullable().optional().default(null),
    options: z
        .array(z.object({ key: z.string(), display_name: z.string() }))
        .nullable()
        .optional()
        .default(null),
    default_value: z.unknown().nullable().optional().default(null),
    depends_on: z.array(z.string()).nullable().optional().default(null),
    applicable_when: z.array(z.record(z.unknown())).nullable().optional().default(null),
});

// Report-level KPI catalog entry (Plan Metric view).
export const kpiSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    sortable: z.boolean().optional().default(true),
});

export const promoMetricFieldTypeSchema = z.enum(['number', 'string', 'date']);

/**
 * One selectable KPI of the Promo Metric report
 * (`promo_metric_grid.requestable_kpis[]`). This is the backend's own list of
 * keys the promo-metric endpoints will serve, so it is NOT derivable from
 * `metrics[]`: it also covers grid-only columns (`rate_per_case`), event/mechanic
 * FYI fields (`offer_type`, `performance_start_date`) and derived metrics.
 */
export const promoMetricGridKpiSchema = z.object({
    key: z.string().min(1),
    display_name: z.string().min(1),
    order: z.number().optional().default(0),
    default_visible: z.boolean().optional().default(false),
    field_type: promoMetricFieldTypeSchema.optional().default('number').catch('number'),
});

/**
 * Promo Metric report contract. Only the keys the FE consumes are declared; the
 * backend's calculation-engine keys (`day_metrics`, `derived_metrics`,
 * `spend_bucket_outputs`, ...) pass through untouched.
 */
export const promoMetricGridSchema = z
    .object({
        requestable_kpis: z.array(promoMetricGridKpiSchema).optional().default([]),
    })
    .passthrough();

export const customerConfigSchema = z.object({
    // Required — the FE cannot render the module without these.
    metrics: z.array(metricSchema).min(1),
    metric_groups: z.array(metricGroupSchema).min(1),
    // Optional + loosely typed: the FE no longer consumes `metric_views` at
    // runtime (the KPI catalog is derived from `metrics` group `kpi_metrics`),
    // so neither its absence nor its internal shape blocks the module.
    metric_views: z.array(metricViewSchema).optional(),
    // Optional: a backend that has not shipped the promo-metric contract yet
    // leaves the Promo Metric KPI selector on the `metrics[]`-derived catalog.
    promo_metric_grid: promoMetricGridSchema.optional(),
    fe_config: feConfigSchema,
    promo_library: promoLibrarySchema.optional(),
    promotion_types: z.array(promotionTypeSchema).min(1),
    tactics: z.array(tacticSchema).min(1),
    // Generic attribute schema driving the dynamic `attributes` codec. Tolerant:
    // defaults to [] so a not-yet-migrated backend does not block the module
    // (the codec falls back to field-config-derived keys when empty).
    entity_attributes: z.array(entityAttributeSchema).optional().default([]),
    dim1_label: z.string().min(1),
    dim2_label: z.string().min(1),
    currency_code: z.string().min(1),
    // Tolerant — defaulted so absence does not block the module.
    kpis: z.array(kpiSchema).optional().default([]),
    year_label: z.string().optional(),
    offer_methods: z.array(z.unknown()).optional().default([]),
    validation_rules: z.array(z.unknown()).optional().default([]),
    visibility_profiles: z.record(z.unknown()).optional().default({}),
});

export type ZodIssueLike = {
    path: (string | number)[];
    message: string;
};

/**
 * Thrown when the config response (live or fallback) fails schema validation
 * or referential-integrity checks in an adapter. Carrying the issue list lets
 * the gate render the offending field paths for debugging.
 */
export class ConfigValidationError extends Error {
    readonly issues: ZodIssueLike[];

    constructor(issues: ZodIssueLike[]) {
        const summary = issues
            .slice(0, 5)
            .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
            .join('; ');
        super(`TPO Gen config validation failed: ${summary}`);
        this.name = 'ConfigValidationError';
        this.issues = issues;
    }
}
